import { useQuery } from '@tanstack/react-query';
import { Activity, Cpu, Database, HardDrive, RefreshCw, Server, Timer } from 'lucide-react';
import { ComponentType } from 'react';
import Button from '@/components/ui/Button';
import { systemMetricsService } from '@/services/systemMetricsService';

const formatBytes = (value?: number | null) => {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return 'N/D';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatPercent = (value?: number | null) => {
  if (value == null || !Number.isFinite(value)) {
    return 'N/D';
  }

  return `${(value * 100).toFixed(1)}%`;
};

const formatNumber = (value?: number | null) => {
  if (value == null || !Number.isFinite(value)) {
    return 'N/D';
  }

  return value.toFixed(0);
};

const formatDuration = (seconds?: number | null) => {
  if (seconds == null || !Number.isFinite(seconds)) {
    return 'N/D';
  }

  const totalSeconds = Math.floor(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

interface MetricCardProps {
  title: string;
  value: string;
  detail?: string;
  icon: ComponentType<{ className?: string }>;
}

const MetricCard = ({ title, value, detail, icon: Icon }: MetricCardProps) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
        {detail && <p className="mt-1 text-sm text-gray-500">{detail}</p>}
      </div>
      <div className="rounded-md bg-gray-100 p-2 text-gray-600">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const usagePercent = (used: number, max: number) => {
  if (!max || max <= 0) {
    return null;
  }

  return used / max;
};

const SystemMetrics = () => {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['system-metrics'],
    queryFn: () => systemMetricsService.getSystemMetrics(),
    refetchInterval: 30_000,
  });

  const heapUsage = data ? usagePercent(data.memory.heapUsedBytes, data.memory.heapMaxBytes) : null;
  const nonHeapUsage = data ? usagePercent(data.memory.nonHeapUsedBytes, data.memory.nonHeapMaxBytes) : null;
  const dbUsage = data?.database.activeConnections != null && data?.database.maxConnections
    ? data.database.activeConnections / data.database.maxConnections
    : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Métricas del sistema</h1>
          <p className="mt-2 text-sm text-gray-600">
            Datos internos de la aplicación. Se actualizan automáticamente cada 30 segundos.
          </p>
          {data?.timestamp && (
            <p className="mt-1 text-xs text-gray-500">
              Última lectura: {new Date(data.timestamp).toLocaleString()}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          loading={isFetching}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          No fue posible cargar las métricas del sistema.
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
          Cargando métricas...
        </div>
      ) : data && (
        <div className="space-y-8">
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Recursos</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                title="CPU proceso"
                value={formatPercent(data.cpu.processUsage)}
                detail={`${data.cpu.availableProcessors} procesadores disponibles`}
                icon={Cpu}
              />
              <MetricCard
                title="CPU sistema"
                value={formatPercent(data.cpu.systemUsage)}
                icon={Activity}
              />
              <MetricCard
                title="Memoria heap"
                value={formatPercent(heapUsage)}
                detail={`${formatBytes(data.memory.heapUsedBytes)} de ${formatBytes(data.memory.heapMaxBytes)}`}
                icon={HardDrive}
              />
              <MetricCard
                title="Memoria non-heap"
                value={formatPercent(nonHeapUsage)}
                detail={`${formatBytes(data.memory.nonHeapUsedBytes)} de ${formatBytes(data.memory.nonHeapMaxBytes)}`}
                icon={Server}
              />
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Base de datos y JVM</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                title="Conexiones DB activas"
                value={formatNumber(data.database.activeConnections)}
                detail={`Uso: ${formatPercent(dbUsage)} · Máx: ${formatNumber(data.database.maxConnections)}`}
                icon={Database}
              />
              <MetricCard
                title="Conexiones DB en espera"
                value={formatNumber(data.database.pendingConnections)}
                detail={`Inactivas: ${formatNumber(data.database.idleConnections)}`}
                icon={Database}
              />
              <MetricCard
                title="Threads activos"
                value={formatNumber(data.threads.live)}
                detail={`Daemon: ${formatNumber(data.threads.daemon)} · Pico: ${formatNumber(data.threads.peak)}`}
                icon={Activity}
              />
              <MetricCard
                title="Tiempo encendida"
                value={formatDuration(data.uptime.processUptimeSeconds)}
                detail={`Ready: ${formatDuration(data.uptime.applicationReadyTimeSeconds)}`}
                icon={Timer}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default SystemMetrics;
